'use client';

import { memo, useCallback } from 'react';

import { type Asset, type Workspace } from '@air/api/types';
import { Modal, ModalCloseButton, ModalDescription, ModalTitle } from '@air/primitive-modal';

import { Button, type ButtonProps } from '~/components/Button';
import { useCreateCdnLink } from '~/hooks/api/useCreateCdnLink';
import { useUpdateCdnLink } from '~/hooks/api/useUpdateCdnLink';
import { useTrackCreatedCdnLink } from '~/hooks/events/useTrackCreatedCdnLink';

export type GenerateCdnLinkModalProps = {
  onClose: () => void;
  assetId: Asset['id'];
  workspaceId: Workspace['id'];
  /** If set, reactivates an existing inactive CDN link instead of creating a new one */
  inactiveCdnLinkId?: string;
  onSuccess: () => void;
  onError: () => void;
  /** Size override for the Cancel / confirm CTAs. Defaults to "large" to match the other plugin integrations. */
  buttonSize?: ButtonProps['size'];
};

export const GenerateCdnLinkModal = memo(
  ({
    onClose,
    assetId,
    workspaceId,
    inactiveCdnLinkId,
    onSuccess,
    onError,
    buttonSize = 'large',
  }: GenerateCdnLinkModalProps) => {
    const isReactivation = !!inactiveCdnLinkId;

    const createCdnLink = useCreateCdnLink();
    const updateCdnLink = useUpdateCdnLink();
    const { trackCreatedCdnLink } = useTrackCreatedCdnLink();

    const isPending = createCdnLink.isPending || updateCdnLink.isPending;

    const handleConfirm = useCallback(() => {
      const action = inactiveCdnLinkId ? 'reactivate' : 'create';
      const mutationOptions = {
        onError,
        onSuccess: () => {
          trackCreatedCdnLink({ action, asset_id: assetId });
          onSuccess();
        },
      };

      if (inactiveCdnLinkId) {
        updateCdnLink.mutate({ cdnLinkId: inactiveCdnLinkId, inactive: false, workspaceId }, mutationOptions);
      } else {
        createCdnLink.mutate({ assetId, workspaceId }, mutationOptions);
      }
    }, [
      assetId,
      createCdnLink.mutate,
      inactiveCdnLinkId,
      onError,
      onSuccess,
      trackCreatedCdnLink,
      updateCdnLink.mutate,
      workspaceId,
    ]);

    const handleDismiss = useCallback(() => {
      if (!isPending) {
        onClose();
      }
    }, [isPending, onClose]);

    const getConfirmLabel = (): string => {
      if (isReactivation) {
        return isPending ? 'Reactivating...' : 'Reactivate link';
      }

      return isPending ? 'Creating...' : '+ Create link';
    };

    const confirmLabel = getConfirmLabel();

    return (
      <Modal isOpen onDismiss={handleDismiss}>
        <div className="flex items-start justify-between">
          <ModalTitle>{isReactivation ? 'Reactivate CDN link?' : 'Generate a CDN link?'}</ModalTitle>
          <ModalCloseButton disabled={isPending} onClick={handleDismiss} />
        </div>

        <ModalDescription className="mt-4">
          {isReactivation
            ? 'A previously deactivated CDN link exists for this asset. Reactivating it will allow the URL to automatically update when a new version is uploaded.'
            : "Generate a secure, evergreen link to this file. You'll need to generate this link before inserting it into your CMS."}
        </ModalDescription>

        <div className="mt-4 flex items-center justify-end gap-3">
          <Button appearance="outline" disabled={isPending} onClick={handleDismiss} size={buttonSize}>
            Cancel
          </Button>
          <Button appearance="primary" autoFocus disabled={isPending} onClick={handleConfirm} size={buttonSize}>
            {confirmLabel}
          </Button>
        </div>
      </Modal>
    );
  },
);

GenerateCdnLinkModal.displayName = 'GenerateCdnLinkModal';
